import type { Course } from "./content/course.ts";
import { itemIdOf, skillOf } from "./content/types.ts";
import type { LessonDef, Recipe, RecipeStep, SkillId, UnitDef } from "./content/types.ts";
import { exerciseType } from "./exercises/registry.ts";
import { poolFor, reviewPools, type LessonStep } from "./lesson.ts";
import { started } from "./progress/query.ts";
import { SILENT } from "./review-table.ts";
import type { GameState } from "./state.ts";

/** Whether `step` needs a recording to be answered, by its effective skill and options. */
export const stepByEar = (recipe: Recipe, step: RecipeStep): boolean =>
	exerciseType(step.type).byEar({ skill: step.skill ?? recipe.skill, options: step.options ?? {} });

/** Whether the lesson teaches by sound: "Can't listen now" closes it rather than muting it. */
export const teaches = (recipe: Recipe): boolean => recipe.sound === "teaches";

/**
 * Whether a lesson of this recipe can run with listening off. Every by-ear
 * step must declare its alternative: a release without that declaration falls
 * back to "needs sound" rather than serving a listening step to a muted learner.
 */
export const canMute = (recipe: Recipe): boolean =>
	recipe.sound === "drops" ||
	(recipe.sound === undefined &&
		recipe.steps.every((s) => !stepByEar(recipe, s) || s.silent !== undefined));

/** The declared alternative of the by-ear step of this type and skill, if any. */
export const silentStepFor = (
	recipe: Recipe,
	kind: string,
	skill: SkillId,
): Omit<RecipeStep, "silent"> | "drop" | undefined =>
	recipe.steps.find(
		(s) => s.type === kind && (s.skill ?? recipe.skill) === skill && stepByEar(recipe, s),
	)?.silent;

/** A round's step as a learner with listening off sees it: itself, a stand-in, or nothing. */
export type Silencer = (step: LessonStep) => LessonStep | null;

/**
 * The muted build of a round. Callers build normally and silence afterwards, so
 * the unmuted path draws exactly the randomness it always did.
 */
export const silence = (steps: LessonStep[], s: Silencer): LessonStep[] =>
	steps.flatMap((x) => s(x) ?? []);

/** Silences a recipe lesson through the alternatives its recipe declares. */
export function lessonSilencer(lesson: LessonDef, unit: UnitDef, course: Course): Silencer {
	const recipe = course.recipeById(lesson.recipe);
	if (recipe.sound === "drops") return () => null;
	return (step) => {
		if (!exerciseType(step.kind).heard(step)) return step;
		// A heard step with no key (an audio match) has no item to stand in for.
		if (!("key" in step)) return null;
		const alt = silentStepFor(recipe, step.kind, skillOf(step.key));
		if (alt === undefined || alt === "drop") return null;
		const item = course.itemById(itemIdOf(step.key));
		const skill = alt.skill ?? recipe.skill;
		return exerciseType(alt.type).build({
			itemId: item.id,
			pool: poolFor(item, unit, course, alt),
			options: { ...alt.options, skill },
			course,
			unit,
		});
	};
}

/** Silences a review round (practice, key review) through the review table. */
export function reviewSilencer(st: GameState, course: Course): Silencer {
	const pools = reviewPools(
		course.units.filter((u) => started(st, u)),
		course,
	);
	return (step) => {
		if (!exerciseType(step.kind).heard(step)) return step;
		if (!("key" in step)) return null;
		const build = SILENT[`${course.itemById(itemIdOf(step.key)).kind}:${skillOf(step.key)}`];
		return build ? build(itemIdOf(step.key), pools, course) : null;
	};
}
